import { RecordCountRequest } from '../models/cycle-count-session.model';

/**
 * One cycle-count record-count request that failed to reach the server and is
 * queued for replay when connectivity returns. Keyed by (cycleCountId, itemId):
 * a later count for the same item supersedes the earlier queued one.
 */
export interface PendingCount {
  cycleCountId: string;
  itemId: string;
  request: RecordCountRequest;
}

/**
 * Upserts a pending count into the offline queue by (cycleCountId, itemId): if an
 * entry for that item is already queued it is replaced with the newer request,
 * otherwise the entry is appended. Returns a NEW array — never mutates the input.
 * The replace-not-append rule is what stops a counter who re-keys the same item
 * offline from queuing several stale writes for it.
 */
export function upsertPendingCount(
  queue: PendingCount[],
  entry: PendingCount
): PendingCount[] {
  const idx = queue.findIndex(
    (e) => e.cycleCountId === entry.cycleCountId && e.itemId === entry.itemId
  );
  if (idx < 0) return [...queue, entry];
  const next = [...queue];
  next[idx] = entry;
  return next;
}

/**
 * Removes the queued entry for one (cycleCountId, itemId) — used after a replay
 * of that entry succeeds. Returns a new array; a no-match input is returned
 * unchanged (as a copy).
 */
export function removePendingCount(
  queue: PendingCount[],
  cycleCountId: string,
  itemId: string
): PendingCount[] {
  return queue.filter(
    (e) => !(e.cycleCountId === cycleCountId && e.itemId === itemId)
  );
}

/**
 * Selects the queued entries belonging to one session, so replay only re-sends
 * counts for the session currently open (a shared device may hold queued counts
 * for several sessions).
 */
export function selectPendingForSession(
  queue: PendingCount[],
  cycleCountId: string
): PendingCount[] {
  return queue.filter((e) => e.cycleCountId === cycleCountId);
}
